import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { COGNITO_CONFIG } from 'constants/localStorageConstants';
import { COGNITO_REDIRECT_URI } from 'constants/environmentVariables';
import {
	getDefaultDomainConfigObj,
	checkRedirectURIAllowed,
	mapDomainToValues,
	getDomainFromInput
} from 'components/authentication/authenticationUtils';
import { amplifyResetPassword, amplifyForgotPassword } from 'actions/authentication';
import {
	Button,
	IconButton,
	InputAdornment,
	LinearProgress,
	TextField,
	Link,
	Grid,
	Divider,
	Box,
	useTheme
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { authenticationPagesStyles } from 'constants/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getQueryParamsFromLocation, stringifyQueryObj } from 'utils/urlHelpers';
import { Controller, useForm } from 'react-hook-form';
import ErrorMessage from 'utils/errorMessages';
import { objectHasKeys } from 'utils/helpers';
import {
	validateEmailRegex,
	validatePasswordShape,
	validateSixDigitCode
} from 'utils/validateForm';
import AloftLogo from 'assets/images/aloft_logo_white.png';
import AirControl from 'assets/images/air_control_white.png';
import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { addSnackbarAlerts } from 'reducers/globalSlice';
import {
	requestingResetCodeSelector,
	resetCodeSentSelector,
	setRequestingResetCode,
	setResetCodeSent
} from 'reducers/userAuthSlice';

const PasswordRequirementsChecklist = lazy(
	() => import('components/authentication/PasswordRequirementsChecklist')
);
const AuthenticationFooter = lazy(() => import('components/authentication/AuthenticationFooter'));
const LanguageSwitcherWrap = lazy(() => import('components/elements/LanguageSwitcherWrap'));

const ResetPassword = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();
	const theme = useTheme();
	const styles = {
		...authenticationPagesStyles(theme)
	};
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const queryParameters = queryString.parse(window.location.search);
	const queryParameterEmail = queryParameters.email;
	const autoSubmit = Boolean(queryParameterEmail);
	const [showLoader, setShowLoader] = useState(autoSubmit);
	const [passwordFieldFocused, setPasswordFieldFocused] = useState(false);
	const requestingResetCode = useSelector(requestingResetCodeSelector);
	const resetCodeSent = useSelector(resetCodeSentSelector);

	const parseRedirectURI = () => {
		const queryParameters_ = getQueryParamsFromLocation(location);
		return queryParameters_.success_redirect_uri;
	};
	const successRedirectURI = parseRedirectURI();

	const createCognitoConfig = () => {
		const { client_id, pool, host } = getDefaultDomainConfigObj();

		return {
			clientId: client_id,
			userPoolId: pool,
			redirectURI: COGNITO_REDIRECT_URI,
			successRedirectURI,
			host
		};
	};

	const cognitoConfig = createCognitoConfig();

	const {
		formState: { errors },
		control,
		handleSubmit,
		getValues,
		watch,
		trigger
	} = useForm({
		defaultValues: {
			code: '',
			password: '',
			confirmPassword: '',
			email: queryParameterEmail
		}
	});

	const passwordCurrentValue = watch('password');
	const confirmPasswordCurrentValue = watch('confirmPassword');
	const emailCurrentValue = watch('email');

	const clearEmailFromQueryParameter = useCallback(() => {
		const updatedQueryParameters = {
			...queryParameters
		};
		delete updatedQueryParameters.email;
		navigate({
			pathname: '/reset-password',
			search: stringifyQueryObj(updatedQueryParameters)
		});
	}, [queryParameters, navigate]);

	const handleRequestResetCode = useCallback(async () => {
		setShowLoader(true);
		const { email } = getValues();
		const domain = getDomainFromInput(email);
		const newDomainValues = mapDomainToValues(domain);
		if (newDomainValues) {
			//Domain is SSO - show message to have them change password with SSO
			dispatch(
				addSnackbarAlerts([
					{
						type: 'error',
						message: `${t('Your password for')} ${email} ${t(
							'is managed through your single sign-on (SSO) provider'
						)}. ${t('Please reset your password through them')}.`,
						duration: 20_000
					}
				])
			);
			clearEmailFromQueryParameter();
			setShowLoader(false);
		} else {
			dispatch(setRequestingResetCode(true));
			const res = await dispatch(
				amplifyForgotPassword({
					username: email,
					config: cognitoConfig,
					navigate,
					queryParams: queryParameters
				})
			);
			if (res) {
				dispatch(setResetCodeSent(true));
			} else {
				//To prevent infinite loop of trying to request code
				clearEmailFromQueryParameter();
			}
			dispatch(setRequestingResetCode(false));
			setShowLoader(false);
		}
	}, [
		dispatch,
		getValues,
		navigate,
		queryParameters,
		t,
		cognitoConfig,
		clearEmailFromQueryParameter
	]);

	const resetPassword = async () => {
		const { password, code, email } = getValues();
		if (!successRedirectURI || checkRedirectURIAllowed(successRedirectURI)) {
			localStorage.setItem(COGNITO_CONFIG, JSON.stringify(cognitoConfig));
			setShowLoader(true);
			await dispatch(
				amplifyResetPassword({
					username: email,
					newPassword: password,
					code,
					config: cognitoConfig,
					navigate,
					queryParams: queryParameters
				})
			);
			setShowLoader(false);
		}
	};

	const handleClickShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const handleClickShowConfirmPassword = () => {
		setShowConfirmPassword(!showConfirmPassword);
	};

	const passwordsMatch = () => {
		const { password, confirmPassword } = getValues();
		return password && confirmPassword && password === confirmPassword;
	};

	const handleClickLogin = () => {
		navigate(`/login`);
	};

	const handleEmailKeyDown = e => {
		// Trigger request code on Enter
		if (e.keyCode === 13) {
			handleRequestResetCode();
		}
	};

	const hasErrors = objectHasKeys(errors);

	const handleAlreadyHaveCode = () => {
		dispatch(setResetCodeSent(true));
	};

	useEffect(() => {
		const validatePasswords = async () => {
			await trigger(['password', 'confirmPassword']);
		};
		if (passwordCurrentValue || confirmPasswordCurrentValue) {
			validatePasswords();
		}
	}, [passwordCurrentValue, confirmPasswordCurrentValue, trigger]);

	useEffect(() => {
		if (autoSubmit && !requestingResetCode && !resetCodeSent) {
			handleRequestResetCode();
		}
	}, [autoSubmit, handleRequestResetCode, requestingResetCode, resetCodeSent]);

	useEffect(() => {
		return () => {
			dispatch(setResetCodeSent(false));
		};
	}, [dispatch]);

	return (
		<Suspense fallback={null}>
			<LanguageSwitcherWrap>
				<Box sx={styles.main}>
					<Box sx={styles.headerContainer}>
						<Box
							component={'img'}
							src={AloftLogo}
							alt=''
							onClick={handleClickLogin}
							sx={styles.logo}
						/>
						<Link
							href='https://www.aloft.ai/support/'
							target='_blank'
							rel='noopener noreferrer'
							underline='none'
						>
							<Button
								sx={{ ...styles.outlineButton, ...styles.topSupportButton }}
								variant='outlined'
							>
								{t('Support')}
							</Button>
						</Link>
					</Box>
					<Grid container spacing={2} sx={styles.mainContainer}>
						<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.leftContainer}>
							<Box component={'img'} src={AirControl} alt='' sx={styles.airControlLogo} />
						</Grid>
						{!resetCodeSent && (
							<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
								<Box sx={styles.outlineContainer}>
									<Box sx={styles.formTitle}>
										{t('Forgot Password')}
										{showLoader && <LinearProgress />}
									</Box>
									{!autoSubmit && (
										<>
											<Controller
												render={({ field }) => (
													<TextField
														{...field}
														placeholder={t('Email')}
														variant='outlined'
														disabled={showLoader}
														sx={styles.textFieldNoMargin}
														autoFocus
														onKeyDown={handleEmailKeyDown}
													/>
												)}
												name='email'
												control={control}
												rules={{
													required: true,
													validate: {
														emailAddress: validateEmailRegex
													}
												}}
											/>
											<ErrorMessage name='email' error={errors.email} sx={styles.error} />
											<Button
												color='primary'
												disabled={showLoader || hasErrors || !getValues().email}
												sx={styles.button}
												variant='contained'
												onClick={handleRequestResetCode}
											>
												{t('Request Password Reset Code')}
											</Button>
											<Divider sx={styles.or}>{t('OR')}</Divider>
											<Button
												color='primary'
												sx={styles.button}
												variant='contained'
												onClick={handleAlreadyHaveCode}
											>
												{t('I Already Have a Code')}
											</Button>
										</>
									)}
								</Box>
							</Grid>
						)}
						{resetCodeSent && (
							<Grid item xs={12} sm={12} md={12} lg={6} sx={styles.rightContainer}>
								<Box
									component={'form'}
									onSubmit={handleSubmit(resetPassword)}
									elevation={0}
									sx={styles.outlineContainer}
								>
									<Box sx={styles.formTitle}>
										{t('Reset Password')}
										{showLoader && <LinearProgress />}
									</Box>
									<>
										<>
											<Controller
												render={({ field }) => (
													<TextField
														{...field}
														placeholder={t('Email')}
														variant='outlined'
														disabled={showLoader}
														sx={styles.textFieldNoMargin}
														autoFocus
													/>
												)}
												name='email'
												control={control}
												rules={{
													required: true,
													validate: {
														emailAddress: validateEmailRegex
													}
												}}
											/>
											<ErrorMessage name='email' error={errors.email} sx={styles.error} />
										</>
										<Controller
											render={({ field }) => (
												<TextField
													{...field}
													disabled={showLoader}
													placeholder={t('Reset Code')}
													variant='outlined'
													sx={styles.textField}
												/>
											)}
											name='code'
											control={control}
											rules={{
												required: true,
												validate: {
													sixDigitCode: validateSixDigitCode
												}
											}}
										/>
										<ErrorMessage name='code' error={errors.code} sx={styles.error} />
										<Button
											tabIndex={-1}
											sx={styles.forgotPassword}
											onClick={handleRequestResetCode}
											disabled={!emailCurrentValue}
										>
											{t('Resend code?')}
										</Button>
										<Controller
											render={({ field }) => (
												<TextField
													{...field}
													onFocus={() => setPasswordFieldFocused(true)}
													placeholder={t('New Password')}
													inputProps={{ type: showPassword ? 'text' : 'password' }}
													variant='outlined'
													disabled={showLoader}
													sx={styles.textField}
													InputProps={{
														endAdornment: (
															<InputAdornment position='end'>
																<IconButton onClick={handleClickShowPassword}>
																	{showPassword ? <Visibility /> : <VisibilityOff />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
											name='password'
											control={control}
											rules={{
												required: true,
												validate: {
													passwordShape: validatePasswordShape,
													passwordMismatch: passwordsMatch
												}
											}}
										/>
										{(passwordFieldFocused || errors.password) && (
											<Suspense fallback={null}>
												<PasswordRequirementsChecklist value={passwordCurrentValue} />
											</Suspense>
										)}
										<Controller
											render={({ field }) => (
												<TextField
													{...field}
													placeholder={t('Confirm New Password')}
													inputProps={{ type: showConfirmPassword ? 'text' : 'password' }}
													variant='outlined'
													disabled={showLoader}
													sx={styles.textField}
													InputProps={{
														endAdornment: (
															<InputAdornment position='end'>
																<IconButton onClick={handleClickShowConfirmPassword}>
																	{showConfirmPassword ? <Visibility /> : <VisibilityOff />}
																</IconButton>
															</InputAdornment>
														)
													}}
												/>
											)}
											name='confirmPassword'
											control={control}
											rules={{
												required: true,
												validate: {
													passwordMismatch: passwordsMatch
												}
											}}
										/>
										<ErrorMessage
											name='confirmPassword'
											error={errors.confirmPassword}
											sx={styles.error}
										/>
										<Button
											color='primary'
											disabled={showLoader || hasErrors}
											sx={styles.button}
											variant='contained'
											type='submit'
										>
											{t('Submit')}
										</Button>
									</>
								</Box>
								<Box sx={styles.belowSignIn}>
									<Link
										href='https://www.aloft.ai/support/'
										target='_blank'
										rel='noopener noreferrer'
										underline='none'
										sx={styles.bottomSupportButton}
									>
										<Button sx={styles.outlineButton} variant='outlined' fullWidth>
											{t('Support')}
										</Button>
									</Link>
								</Box>
							</Grid>
						)}
					</Grid>
					<Suspense fallback={null}>
						<AuthenticationFooter />
					</Suspense>
				</Box>
			</LanguageSwitcherWrap>
		</Suspense>
	);
};

export default ResetPassword;
